import { useCallback, useEffect, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import HelpButton from 'Components/HelpButton';

import CreateReview from 'Pages/WebDashboard/CreateReview';

import { setUserProfile } from 'Redux/Actions/userActions';
import actionTypes from 'Redux/constants';

import { ROUTES } from 'routes/route.constants';

import { getSingleDesignAudit } from 'services/webdashboardReviews';

import { errorReactToastify } from 'utlis/toasts';

import DashboardHeader from './components/DashboardHeader';
import Overview from './components/Overview';
import ReviewsSection from './components/ReviewsSection';
import Sidebar from './components/Sidebar';

import styles from './styles.module.scss';

const DashboardHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationObject = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isCreateReviewOpen, setIsCreateReviewOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [isLoadingReview, setIsLoadingReview] = useState(false);

  const authUser = useSelector(state => state.userStore);

  // Handle opening review popup for existing review
  const handleViewReview = review => {
    setSelectedReview(review);
    setIsCreateReviewOpen(true);
  };

  // Handle closing review popup
  const handleCloseReview = () => {
    setIsCreateReviewOpen(false);
    setSelectedReview(null);
  };

  // Handle opening new review popup
  const handleCreateNewReview = useCallback(() => {
    setSelectedReview(null);
    setIsCreateReviewOpen(true);
  }, []);

  // Fetch review by UUID and open it
  const fetchAndOpenReview = useCallback(async reviewId => {
    if (!reviewId) {
      return;
    }

    setIsLoadingReview(true);

    try {
      const reviewData = await getSingleDesignAudit(reviewId);

      if (reviewData && typeof reviewData === 'object' && reviewData.uuid) {
        setSelectedReview(reviewData);
        setIsCreateReviewOpen(true);

        // Clean up URL parameters
        setSearchParams({});
      }
      else {
        setSearchParams({});
      }
    }
    catch (error) {
      // Clear URL parameters even on error
      setSearchParams({});
      errorReactToastify(
        'This design audit does not exist or you don\'t have permission to view it.',
        {
          autoClose: 5000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#FDF0F0', width: '530px' },
        }
      );
    }
    finally {
      setIsLoadingReview(false);
    }
  }, [setSearchParams]);

  const checkAuthentication = async () => {
    try {
      const userObjectString = localStorage.getItem('userObject');

      if (!userObjectString) {
        // Pass current URL parameters to login route
        const loginUrl = locationObject.search ? `${ROUTES.WEB_LOGIN}${locationObject.search}` : ROUTES.WEB_LOGIN;

        navigate(loginUrl);

        return;
      }

      const userObject = JSON.parse(userObjectString);

      if (!userObject || !userObject.uuid) {
        // Pass current URL parameters to login route
        const loginUrl = locationObject.search ? `${ROUTES.WEB_LOGIN}${locationObject.search}` : ROUTES.WEB_LOGIN;

        navigate(loginUrl);

        return;
      }

      // Dispatch userObject to Redux to populate userStore
      dispatch({
        type: actionTypes.SIGN_IN_USER,
        payload: userObject,
      });
      setIsCheckingAuth(false);
    }
    catch (error) {
      // Handle invalid JSON in localStorage
      // eslint-disable-next-line no-console
      console.error('Error parsing userObject from localStorage:', error);
      localStorage.removeItem('userObject');

      const loginUrl = locationObject.search ? `${ROUTES.WEB_LOGIN}${locationObject.search}` : ROUTES.WEB_LOGIN;

      navigate(loginUrl);
    }
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (authUser && authUser.uuid) {
      dispatch(setUserProfile(authUser));
    }
  }, [authUser?.uuid]);

  // Check localStorage for modal state on component mount (only after auth check)
  useEffect(() => {
    // Wait for auth check to complete and ensure no URL parameters are being processed
    if (isCheckingAuth) {
      return;
    }

    const shouldReopenModal = localStorage.getItem('designReview_shouldReopenModal') === 'true';
    const hasUrlParams = searchParams.get('createReview') && searchParams.get('openReview');

    if (shouldReopenModal && !hasUrlParams) {
      handleCreateNewReview();

      // Clear the localStorage flags after opening modal
      localStorage.removeItem('designReview_shouldReopenModal');
    }
  }, [handleCreateNewReview, isCheckingAuth, searchParams]);

  // Handle URL parameters on component mount
  useEffect(() => {
    const createReviewParam = searchParams.get('createReview');
    const openReviewParam = searchParams.get('openReview');

    if (createReviewParam === 'true' && openReviewParam) {
      // Wait for auth check to complete before fetching review
      if (!isCheckingAuth) {
        fetchAndOpenReview(openReviewParam);
      }
    }
  }, [searchParams, isCheckingAuth, fetchAndOpenReview]);

  // Show loader while checking authentication or loading review data
  if (isCheckingAuth || isLoadingReview) {
    return (
      <div className={styles['loader-container']}>
        <ImpulseSpinner frontColor={'#28BDAE'} />
      </div>
    );
  }

  return (
    <div className={styles['dashboard-container']}>
      <div className="min-h-screen bg-gray-50 grid gap-0 flex w-full">
        <Sidebar
          authUser={authUser}
        />
        <div className="flex flex-1 bg-gray-50 p-2 pl-0 max-h-screen">
          <div className="flex flex-1 flex-col overflow-auto rounded-lg bg-white shadow-figma-sm relative">
            <DashboardHeader onCreateReviewToggle={handleCreateNewReview} />

            <div className="flex flex-1 overflow-auto flex-col px-6 py-6">
              <Overview
                onStatusFilter={setStatusFilter}
                statusFilter={statusFilter}
              />

              <ReviewsSection
                isCreateReviewOpen={isCreateReviewOpen}
                statusFilter={statusFilter}
                onClearStatusFilter={() => setStatusFilter(null)}
                onViewReview={handleViewReview}
                onCreateReviewToggle={handleCreateNewReview}
              />
            </div>

            <HelpButton />
          </div>
        </div>
      </div>

      {/* CreateReview Modal */}
      {isCreateReviewOpen && (
        <div className="fixed inset-0 z-50">
          <CreateReview
            onClose={handleCloseReview}
            existingReviewData={selectedReview}
          />
        </div>
      )}
    </div>
  );
};

export default DashboardHome;
